import { useMemo } from 'react';
import {
  Badge,
  BadgeVariants,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { FieldSection } from '@/components/common';
import { LoadingOverlay } from '@/components/feedback';
import { VERIFICATION_STATUS_TONE } from '@/config/statusTones';
import { useGetDeclarationDetailsTabQuery } from '../api/declarationsApi';
import {
  buildGhgDetailFields,
  buildEmissionSavingFields,
  buildVoluntaryAddOnFields,
} from '../constants/declarationEmissionFields';
import styles from './DeclarationDetailsTabView.module.scss';

// TODO: add the info icons beside the labels that carry one once a Tooltip ships.

/**
 * Props:
 *   title     the card's heading
 *   children  one or more FieldSection columns
 */
const DetailCard = ({ title, children }) => (
  <div className={styles.card}>
    <div className={styles.cardHeader}>
      <Typography
        variant={TypographyVariants.bodySm}
        weight={TypographyWeights.semibold}
        color={TypographyColors.primary}
      >
        {title}
      </Typography>
    </div>
    <div className={styles.cardBody}>{children}</div>
  </div>
);

/**
 * Props:
 *   declarationId  the declaration whose details the tab reads
 */
export const DeclarationDetailsTabView = ({ declarationId }) => {
  const t = useTranslations();

  const { data: details, isLoading } = useGetDeclarationDetailsTabQuery({ id: declarationId });

  const sdFields = useMemo(() => {
    const sd = details?.sdDetails;
    if (!sd) return [];
    return [
      { label: t('FIELD_DECLARATION_NO'), value: sd.declarationNo },
      [
        { label: t('DECLARATIONS_COL_DATE_OF_ISSUANCE'), value: sd.dateOfIssuance },
        { label: t('FIELD_DATE_OF_DISPATCH'), value: sd.dateOfDispatch },
      ],
      [
        { label: t('FIELD_SUPPLIER_CERTIFICATE_NUMBER'), value: sd.supplierCertificateNumber },
        {
          label: t('FIELD_DECLARATION_VERIFICATION_STATUS'),
          value: sd.verificationStatus ? (
            <Badge
              variant={VERIFICATION_STATUS_TONE[sd.verificationStatusCode] ?? BadgeVariants.default}
              withDot
            >
              {sd.verificationStatus}
            </Badge>
          ) : (
            '-'
          ),
        },
      ],
      { label: t('FIELD_NOTE'), value: sd.note },
    ];
  }, [t, details]);

  const generalFields = useMemo(() => {
    const material = details?.materialDetails;
    if (!material) return [];
    return [
      { label: t('FIELD_TYPE_OF_PRODUCT'), value: material.typeOfProduct },
      { label: t('FIELD_TYPE_OF_RAW_MATERIAL'), value: material.typeOfRawMaterial },
      { label: t('FORM_ADDITIONAL_INFORMATION'), value: material.additionalInformation },
      { label: t('FIELD_COUNTRY_OF_ORIGIN'), value: material.countryOfOrigin },
      [
        { label: t('FIELD_QUANTITY'), value: material.quantity },
        { label: t('FIELD_UNIT'), value: material.unit },
      ],
      [
        { label: t('UNIT_TRACKING'), value: material.trackingUnit },
        {
          label: t('CONTRACTS_FORM_DENSITY', {
            weightUnit: material.densityWeightUnit,
            volumeUnit: material.densityVolumeUnit,
          }),
          value: material.density,
        },
      ],
      { label: t('FIELD_EU_RED_COMPLIANT_MATERIAL'), value: material.euRedCompliantMaterial },
      { label: t('FORM_ISCC_COMPLIANT_MATERIAL'), value: material.isccCompliantMaterial },
      { label: t('FORM_CHAIN_OF_CUSTODY_OPTION'), value: material.chainOfCustodyOption },
      {
        label: t('FIELD_COUNTRY_OF_BIOFUEL_PRODUCTION'),
        value: material.countryOfBiofuelProduction,
      },
      {
        label: t('FIELD_START_DATE_OF_BIOFUEL_PRODUCTION'),
        value: material.startDateOfBiofuelProduction,
      },
      { label: t('FORM_START_DATE_OF_BIOLIQUID_USE'), value: material.startDateOfBioliquidUse },
    ];
  }, [t, details]);

  const certificationScopeFields = useMemo(() => {
    const material = details?.materialDetails;
    if (!material) return [];
    return [
      {
        label: t('FIELD_SUSTAINABILITY_CRITERIA_COMPLIANCE'),
        value: material.rawMaterialSustainabilityCriteria,
      },
      { label: t('FORM_INTERMEDIATE_CROP'), value: material.biomassIntermediateCrop },
      { label: t('FORM_LOW_ILUC_RISK_FEEDSTOCK'), value: material.biomassIlucRiskFeedstocks },
      { label: t('FIELD_WASTE_OR_RESIDUE_DEFINITION'), value: material.rawMaterialWasteDefinition },
      {
        label: t('FORM_WASTE_OR_BY_PRODUCT_PERMIT_NUMBER'),
        value: material.wasteAnimalByProductPermitNumber,
      },
      { label: t('FIELD_PRODUCTION_SUPPORT_RECEIVED'), value: material.supportForProduction },
      { label: t('FORM_SUPPORT_NATURE_AND_SCHEME'), value: material.supportNatureAndScheme },
    ];
  }, [t, details]);

  const ghgDetailFields = useMemo(
    () => (details?.emissionDetails ? buildGhgDetailFields(t, details.emissionDetails) : []),
    [t, details],
  );

  const savingFields = useMemo(
    () => (details?.emissionDetails ? buildEmissionSavingFields(t, details.emissionDetails) : []),
    [t, details],
  );

  const addOnFields = useMemo(
    () => (details?.emissionDetails ? buildVoluntaryAddOnFields(t, details.emissionDetails) : []),
    [t, details],
  );

  if (isLoading || !details) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  return (
    <div className={styles.tab}>
      <DetailCard title={t('DECLARATIONS_DETAIL_CARD_TITLE')}>
        <FieldSection heading={t('SECTION_SUSTAINABILITY_DECLARATION_DETAILS')} fields={sdFields} />
      </DetailCard>

      <DetailCard title={t('DECLARATIONS_DETAIL_CARD_MATERIAL')}>
        <FieldSection heading={t('SECTION_GENERAL_INFORMATION')} fields={generalFields} />
        <FieldSection
          heading={t('SECTION_CERTIFICATION_SCOPE')}
          fields={certificationScopeFields}
        />
      </DetailCard>

      <DetailCard title={t('DECLARATIONS_DETAIL_CARD_EMISSION')}>
        <FieldSection heading={t('SECTION_GHG_EMISSION_DETAILS')} fields={ghgDetailFields} />
        <FieldSection heading={t('SECTION_GHG_EMISSION_SAVING')} fields={savingFields} />
        <FieldSection heading={t('SECTION_VOLUNTARY_ADD_ONS')} fields={addOnFields} />
      </DetailCard>
    </div>
  );
};
